import type { ApiPaymentDto } from './types';

/**
 * Fields the merchant portal reads off a group's payment but must never write back.
 *
 * `updatePayment` on the backend is a whole-object replace, so any edit to a booking has to PUT
 * the payment it just read. That echo has to be trimmed: these fields are owned elsewhere, and
 * re-submitting them ranges from useless to actively breaking.
 *
 *  - completionFiles has its own endpoint and its own column; the payment write ignores it, so
 *    sending it only exposes the request to its validator. A booking whose stored value predates
 *    the {key,name,type} shape failed that validator and made approve unreachable — the merchant
 *    could not act on their own booking because of a field they have no say over.
 *  - generatedInvoiceLink is a presigned S3 URL regenerated per write. Echoing it hands a
 *    short-lived credential back to the server to store.
 *  - insertionOrderReferenceId is derived from the schedule version on each regeneration.
 *  - finalInvoicedAmount is finance-owned. Omitting it leaves the stored value untouched, which
 *    is what we want; echoing it lets a merchant action overwrite a finance number.
 *
 * ioLanguage and ioTemplate are deliberately kept: the backend locks them once the PDF exists but
 * explicitly tolerates a no-op echo of the stored value, and dropping them would look like a
 * change to null.
 */
const SERVER_OWNED_PAYMENT_FIELDS = [
  'completionFiles',
  'generatedInvoiceLink',
  'insertionOrderReferenceId',
  'finalInvoicedAmount',
] as const;

export function stripServerOwnedPaymentFields<T extends ApiPaymentDto | undefined>(payment: T): T {
  if (!payment) return payment;
  const next = { ...(payment as unknown as Record<string, unknown>) };
  for (const field of SERVER_OWNED_PAYMENT_FIELDS) delete next[field];
  return next as unknown as T;
}
